import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, LogIn, LogOut, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmployeeAvatar } from "@/components/shared/EmployeeAvatar";
import { errMsg } from "@/lib/api";
import { beep, Card, OtSelect, savedOt, ScanBox, Screen, useOts } from "./common";
import { cachedGet, cacheRoster, findWorker, normRut, send } from "./offline";

const MSG = { entrada_registrada: "ENTRADA REGISTRADA", salida_registrada: "SALIDA REGISTRADA", rechazado: "No habilitado en la OT", periodo_cerrado: "Período cerrado",
  sin_entrada: "No tiene entrada hoy: marque ENTRADA primero", guardado_sin_senal: "Guardado sin señal · se enviará al volver la señal" };
const STATE = { sin_llegar: "Sin entrada hoy", en_faena: "En faena", salio: "Ya marcó salida" };
const hm = (ts) => new Date(ts).toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit", hour12: false });
const resultMsg = (r, kind) => (r === "ya_registrado" ? `Ya tenía ${kind} registrada hoy` : MSG[r]);

const Choose = ({ p, onPick, onCancel }) => (
  <Card className="space-y-3 border-2 border-[#258F00]" data-testid="terreno-att-choose">
    <div className="flex items-center gap-3">
      {p.worker.id && <EmployeeAvatar id={p.worker.id} v={p.worker.photo_v} name={p.worker.name} size={56} />}
      <div className="min-w-0"><div className="font-extrabold truncate" data-testid="terreno-att-choose-name">{p.worker.name}</div>
        {p.state && <div className="text-sm text-[#5b6656]" data-testid="terreno-att-choose-state">{STATE[p.state]}</div>}</div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[["entrada", "ENTRADA", LogIn], ["salida", "SALIDA", LogOut]].map(([k, l, Icon]) => (
        <button key={k} type="button" data-testid={`terreno-att-pick-${k}`} onClick={() => onPick(k)}
          className={`flex items-center justify-center gap-2 rounded-2xl border-2 py-5 text-lg font-extrabold active:scale-95 transition-transform ${k === "entrada" ? "bg-[#258F00] border-[#258F00] text-white" : "bg-[#17210f] border-[#17210f] text-white"}`}>
          <Icon size={22} />{l}
        </button>))}
    </div>
    <button type="button" data-testid="terreno-att-cancel" onClick={onCancel} className="w-full text-sm font-semibold text-[#5b6656] py-1">Cancelar</button>
  </Card>
);

const Result = ({ last }) => (
  <Card className={`flex items-center gap-3 border-2 ${last.ok ? "border-[#5CD500] bg-[#f3fbe9]" : "border-[#f5c2c0] bg-[#fdf3f2]"}`}>
    {last.ok ? <CheckCircle2 size={36} className="text-[#258F00] shrink-0" /> : <XCircle size={36} className="text-[#b42318] shrink-0" />}
    <div className="min-w-0"><div className={`text-lg font-extrabold ${last.ok ? "text-[#1c6e00]" : "text-[#b42318]"}`} data-testid="terreno-att-last-result">{resultMsg(last.result, last.kind)}{last.ts ? ` · ${hm(last.ts)}` : ""}</div>
      <div className="font-semibold truncate" data-testid="terreno-att-last-name">{last.employee.name}</div></div>
  </Card>
);

export default function Asistencia() {
  const ots = useOts("/rrhh/control-doc/ots");
  const [ot, setOt] = useState(savedOt());
  const [pending, setPending] = useState(null);
  const [last, setLast] = useState(null);
  const [day, setDay] = useState(null);
  const load = useCallback(() => ot && cachedGet(`/rrhh/day/${ot}`).then(setDay).catch(() => setDay(null)), [ot]);
  useEffect(() => { cacheRoster(ot); }, [ot]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const read = (qr) => {
    if (!ot) return toast.error("Seleccione la OT");
    if (pending) return;
    const rut = normRut(qr);
    const row = day?.rows.find((r) => normRut(r.rut) === rut);
    beep(true); setLast(null);
    setPending({ qr, worker: findWorker(ot, qr) || row || { id: null, name: `RUT ${rut}` }, state: row?.state });
  };
  const pick = async (kind) => {
    const { qr, worker } = pending; setPending(null);
    try {
      const r = await send("/rrhh/scan", { qr, ot_id: ot, code: "FAE", kind }, `${kind === "entrada" ? "Entrada" : "Salida"} ${normRut(qr)}`);
      const data = r.queued ? { employee: worker, result: "guardado_sin_senal" } : r.data;
      const ok = r.queued || data.result.endsWith("_registrada");
      beep(ok); setLast({ ...data, kind, ok });
      if (ok) toast.success(resultMsg(data.result, kind)); else toast.error(resultMsg(data.result, kind));
      load();
    } catch (e) { beep(false); if (!e.quota) toast.error(errMsg(e)); }
  };
  const missing = day?.rows.filter((r) => r.state === "sin_llegar") || [];
  return (
    <Screen title="Asistencia" testid="terreno-attendance">
      <OtSelect testid="terreno-att-ot" ots={ots} value={ot} onChange={setOt} />
      {pending && <Choose p={pending} onPick={pick} onCancel={() => setPending(null)} />}
      {ot && <div className={pending ? "hidden" : ""}><ScanBox onRead={read} placeholder="O escriba el RUT y presione Ir" testid="terreno-att-scan" /></div>}
      {last && <Result last={last} />}
      {day && <Card>
        <div className="grid grid-cols-3 text-center mb-3" data-testid="terreno-att-kpis">
          {[["Asignados", day.kpis.asignados, "#17210f"], ["En faena", day.kpis.presentes, "#258F00"], ["Faltan", day.kpis.faltan, "#b42318"]].map(([l, n, c]) => (
            <div key={l}><div className="text-2xl font-extrabold" style={{ color: c }}>{n}</div><div className="text-[11px] text-[#5b6656]">{l}</div></div>))}
        </div>
        <div className="text-xs font-bold uppercase tracking-wider text-[#5b6656] mb-1">Faltan por llegar ({missing.length})</div>
        {missing.map((r) => <div key={r.id} className="py-1.5 text-sm border-b border-[#f0f2ee] last:border-0">{r.name}</div>)}
      </Card>}
    </Screen>
  );
}
